import { Component, Input, Output, EventEmitter, TemplateRef, ContentChild, ChangeDetectionStrategy, ChangeDetectorRef, ElementRef } from '@angular/core';
import { AccordionConfig, AccordionItem } from './accordion-info.interface';

@Component({
  selector: 'usb-accordion-info-product',
  templateUrl: './accordion-info-product.component.html',
  styleUrls: ['./accordion-info-product.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AccordionInfoProductComponent {
  @Input() items: AccordionItem[] = [];
  @Input() categoryId: number = 0;
  @Input() config: AccordionConfig = {
    multiple: false,
    animated: true,
    showArrows: true,
    borderTop: true,
    closeOthers: true
  };

  @Output() itemToggle = new EventEmitter<{ item: AccordionItem; isOpen: boolean; index: number }>();

  @ContentChild('accordionContent') customContentTemplate!: TemplateRef<any>;

  // ✅ Usar un array en lugar de Set para mejor detección de cambios
  private openItemsArray: (string | number)[] = [];

  constructor(private cdr: ChangeDetectorRef, public el: ElementRef) {
    if (this.config.defaultOpen) {
      this.openItemsArray = [...this.config.defaultOpen];
    }
  }

  isOpen(itemId: string | number): boolean {
    return this.openItemsArray.includes(itemId);
  }

  toggleItem(item: AccordionItem, index: number): void {
    if (item.disabled) return;

    const isCurrentlyOpen = this.isOpen(item.id);
    let newOpenItems: (string | number)[];

    if (this.config.multiple) {
      // Permitir múltiples abiertos
      if (isCurrentlyOpen) {
        newOpenItems = this.openItemsArray.filter(id => id !== item.id);
      } else {
        newOpenItems = [...this.openItemsArray, item.id];
      }
    } else {
      // Solo uno abierto a la vez
      if (isCurrentlyOpen) {
        newOpenItems = [];
      } else {
        newOpenItems = [item.id];
      }
    }

    // ✅ Asignar nuevo array para que Angular detecte el cambio
    this.openItemsArray = newOpenItems;

    // ✅ Forzar detección de cambios
    this.cdr.markForCheck();

    this.itemToggle.emit({
      item,
      isOpen: !isCurrentlyOpen,
      index
    });
  }

  // Método para abrir un item específico
  openItem(itemId: string | number): void {
    if (this.config.multiple) {
      if (!this.openItemsArray.includes(itemId)) {
        this.openItemsArray = [...this.openItemsArray, itemId];
        this.cdr.markForCheck();
      }
    } else {
      this.openItemsArray = [itemId];
      this.cdr.markForCheck();
    }
  }

  // Método para cerrar un item específico
  closeItem(itemId: string | number): void {
    this.openItemsArray = this.openItemsArray.filter(id => id !== itemId);
    this.cdr.markForCheck();
  }

  // Método para alternar un item por índice
  toggleByIndex(index: number): void {
    const item = this.items[index];
    if (item) {
      this.toggleItem(item, index);
    }
  }

  // Cerrar todos
  closeAll(): void {
    this.openItemsArray = [];
    this.cdr.markForCheck();
  }

  // Abrir todos (solo si multiple está activado)
  openAll(): void {
    if (this.config.multiple) {
      this.openItemsArray = this.items.map(item => item.id);
      this.cdr.markForCheck();
    }
  }

  paddingAccordion(paddingX: string): string {

    let padding: string = '0';

    switch(this.categoryId) {
      case 1: if(paddingX === 'left') padding = '3.25em'; if(paddingX === 'right') padding = '2.8em'; break;
      case 8: padding = '.75em'; break;
    }

    return padding;
  }

// Método para obtener la altura del contenido de un item específico
getItemContentHeight(index: number): number {
  const accordionElement = this.el.nativeElement;
  const items = accordionElement.querySelectorAll('.accordion-item');
  
  if (items && items[index]) {
    const currentItem = items[index];
    console.log(currentItem)
    // ✅ Usar la clase correcta: 'accordion-content' (la que tienes en tu template)
    const content = currentItem.querySelector('.accordion-content');
    console.log(content)
    if (content) {
      const height = content.scrollHeight;
      return height;
    } else {
      // Fallback: buscar otras posibles clases
      const fallbackContent = currentItem.querySelector('.accordion-content-wrapper');
      if (fallbackContent) {
        const height = fallbackContent.scrollHeight;
        return height;
      }
    }
  }
  
  return 0;
}

// Método específico para obtener la altura de políticas
getPoliciesHeight(): number {
  const accordionElement = this.el.nativeElement;
  
  // Buscar el item de políticas (por título o por contenido)
  const items = accordionElement.querySelectorAll('.accordion-item');
  
  for (let i = 0; i < items.length; i++) {
    const title = items[i].querySelector('.accordion-title')?.textContent;
    if (title?.toLowerCase().includes('política') || title?.toLowerCase().includes('cancelación')) {
      const content = items[i].querySelector('.accordion-content');
      if (content) {
        const height = content.scrollHeight;
        console.log(`📏 Altura de políticas: ${height}px`);
        return height;
      }
    }
  }
  
  // Si no encuentra por título, buscar directamente el componente
  const policies = accordionElement.querySelector('usb-cancellation-policies');
  if (policies) {
    const height = policies.scrollHeight;
    return height;
  }
  
  return 0;
}

// Método para obtener la altura por ID del item
getItemContentHeightById(itemId: string): number {
  const accordionElement = this.el.nativeElement;
  const items = accordionElement.querySelectorAll('.accordion-item');
  
  for (let i = 0; i < items.length; i++) {
    // Verificar si este item tiene el ID buscado
    const header = items[i].querySelector('.accordion-header');
    // O puedes usar un atributo data-id si lo tienes
    if (header?.getAttribute('aria-controls') === `accordion-content-${itemId}`) {
      const content = items[i].querySelector('.accordion-content');
      if (content) {
        return content.scrollHeight;
      }
    }
  }
  
  return 0;
}
}
